import React from "react";
import {
  AuthContextProps,
  AuthProvider,
  AuthProviderProps,
  useAuth,
} from "react-oidc-context";
import {
  SigninRedirectArgs,
  SignoutRedirectArgs,
  User,
  UserManager,
  UserManagerSettings,
  WebStorageStateStore,
} from "oidc-client-ts";

import type { FleetAuthState, SignInOptions, SignInPath } from "./fleetAuth";
import { clearPageScopedFleetStateSession } from "./pageScopedSession";
import { urlFromEnv } from "../utils/urlEnv";

/**
 * App-wide Cognito login, for when REACT_APP_AUTH_PROVIDER=cognito.
 *
 * The same pool and app client the page-scoped Tractor State login already
 * uses -- `us-east-2_SmKdVAL6A` / `fleet_console` in beta -- so no new
 * configuration is needed to turn this on, only the four
 * REACT_APP_FLEET_STATE_COGNITO_* / _API_URL values that a live build already
 * sets. The names are now misleading (the pool is the platform's, not
 * fleet_state's) but renaming them means changing `.env.beta`, `.env.prod` and
 * every local .env; that belongs in stage 5's cleanup, not here.
 *
 * Two differences from `fleetStateAuth.tsx`, which this does not replace:
 *
 *  - `redirect_uri` is `/callback`, the app-wide path both environments
 *    register, rather than `/tractor-state`. Registering `/tractor-state` was
 *    only ever the page-scoped login's need; SW-6943's comment in
 *    platform_auth/infra/envs/prod/main.tf asks for it to be dropped from both
 *    environments once this route ships, which is the follow-up to this change.
 *  - `sessionStorage` becomes `localStorage`, because the session now has to
 *    survive a full-page reload anywhere in the app rather than within one
 *    tab's visit to one page. This is the same reasoning that put Auth0 on
 *    `cacheLocation: "localstorage"` in SW-6329.
 *
 * TWO APP CLIENTS, ONE POOL. The sign-in page's two doors open two clients:
 *
 *   employee -> `fleet_console` (REACT_APP_FLEET_STATE_COGNITO_CLIENT_ID)
 *               with identity_provider=Google, so the hosted card is skipped
 *   client   -> `fleet_console_external` (REACT_APP_COGNITO_EXTERNAL_CLIENT_ID)
 *               which offers native accounts only, so the hosted card it
 *               renders has an email + password form and no Google button
 *
 * Cognito renders the identity providers a client is configured with and its
 * text cannot be edited, so "which client" is the only way to give external
 * users a card without a Google button on it. The door someone chose is
 * remembered in localStorage so that the next page load -- the OAuth callback,
 * every reload after it, and the eventual sign-out -- builds its UserManager
 * for the client that actually issued the session. Until the external client
 * id is configured, both doors use `fleet_console`.
 */
// Normalised: paths are appended to both of these, and a trailing slash in the
// deploy variable turned `/logout` into `//logout` (see utils/urlEnv).
const AUTHORITY = urlFromEnv(
  process.env.REACT_APP_FLEET_STATE_COGNITO_AUTHORITY,
);
// Both client ids are read at call time rather than module load. CRA still
// inlines the values at build time, so a deployed bundle behaves identically;
// the difference is only that a test can set them and exercise the two-client
// path without re-requiring the module (which would hand it a second React --
// see fleetAuth.test.tsx).
const defaultClientId = (): string =>
  process.env.REACT_APP_FLEET_STATE_COGNITO_CLIENT_ID as string;
const externalClientId = (): string | undefined =>
  process.env.REACT_APP_COGNITO_EXTERNAL_CLIENT_ID || undefined;
const HOSTED_UI = urlFromEnv(process.env.REACT_APP_FLEET_STATE_COGNITO_DOMAIN);

/** Where the hosted UI returns to. Registered in both environments. */
export const COGNITO_CALLBACK_PATH = "/callback";

/** localStorage key: which door the current or pending session came through. */
export const SIGN_IN_DOOR_KEY = "fleetconsole.signin.door";

export function cognitoConfigured(): boolean {
  return Boolean(AUTHORITY && defaultClientId() && HOSTED_UI);
}

/**
 * The app client a door opens. `client` gets the external, password-only
 * client when one is configured and falls back to the shared one otherwise,
 * so a build without the variable still signs everyone in.
 */
export function clientIdForDoor(path?: SignInPath): string {
  const external = externalClientId();
  if (path === "client" && external) return external;
  return defaultClientId();
}

export function rememberDoor(path: SignInPath): void {
  try {
    window.localStorage.setItem(SIGN_IN_DOOR_KEY, path);
  } catch {
    // Storage unavailable (private mode quota, disabled): the redirect still
    // works; the callback simply builds for the default client, which is
    // also what a missing external id falls back to.
  }
}

export function rememberedDoor(): SignInPath | undefined {
  try {
    const v = window.localStorage.getItem(SIGN_IN_DOOR_KEY);
    return v === "employee" || v === "client" ? v : undefined;
  } catch {
    return undefined;
  }
}

export function forgetDoor(): void {
  try {
    window.localStorage.removeItem(SIGN_IN_DOOR_KEY);
  } catch {
    // Nothing to forget.
  }
}

/** The client id a fresh page load would build for, from the remembered door. */
export function currentClientId(): string {
  return clientIdForDoor(rememberedDoor());
}

/**
 * The client id the MOUNTED provider was actually built with. Prefer this over
 * `currentClientId()` inside a component: localStorage can move under a live
 * page (another tab signing in through the other door), the provider cannot.
 */
function mountedClientId(auth: { settings?: { client_id?: string } }): string {
  return auth.settings?.client_id ?? currentClientId();
}

export function buildConfig(
  clientId: string = currentClientId(),
): AuthProviderProps {
  const authority = AUTHORITY as string;
  const hostedUi = HOSTED_UI as string;
  return {
    authority,
    client_id: clientId,
    redirect_uri: `${window.location.origin}${COGNITO_CALLBACK_PATH}`,
    // Deliberately NO post_logout_redirect_uri. That is the OIDC RP-initiated
    // logout parameter, and Cognito's /logout does not implement that spec —
    // see `cognitoSignoutArgs` for the shape it does want. Leaving it here
    // would put it back on every sign-out request as noise.
    response_type: "code",
    // Only the OIDC scopes. The resource-server scopes that actually authorize
    // API calls are minted by the pre-token-generation trigger from the
    // caller's groups, and anything unentitled is suppressed there. Requesting
    // them here would be wrong: allowed_oauth_scopes is per-client, so asking
    // for `.../admin` would grant it to whoever asked.
    scope: "openid email profile",
    // Cognito's authorize/token endpoints live on the hosted-UI domain while
    // the issuer and JWKS live on cognito-idp; pin them to avoid discovery
    // quirks.
    metadata: {
      issuer: authority,
      authorization_endpoint: `${hostedUi}/oauth2/authorize`,
      token_endpoint: `${hostedUi}/oauth2/token`,
      userinfo_endpoint: `${hostedUi}/oauth2/userInfo`,
      end_session_endpoint: `${hostedUi}/logout`,
      jwks_uri: `${authority}/.well-known/jwks.json`,
    },
    automaticSilentRenew: true,
    // Cognito has no token-revocation endpoint for this client; don't call one.
    revokeTokensOnSignout: false,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    // Deliberately NO onSigninCallback. `history.replaceState` would change the
    // address bar without telling React Router, leaving it matched on /callback
    // while the URL read "/" -- a blank page after a successful sign-in. The
    // /callback route navigates through the router instead, which both leaves
    // the route and strips ?code=&state=. See components/AuthCallback.
  };
}

/**
 * How each door on the sign-in page reaches Cognito.
 *
 * `employee`: `identity_provider=Google` on /oauth2/authorize. Cognito answers
 * with a 302 straight to accounts.google.com and never renders its hosted
 * card, so staff go console -> Google -> console and are not shown a password
 * form they have no password for. (The page-scoped Tractor State login forced
 * this for everyone until SW-6656; forcing it for everyone was the bug, not
 * the parameter.)
 *
 * `client`: no hint. Managed login ignores `identity_provider=COGNITO` --
 * verified: the card renders identically with and without it -- which is why
 * the external door is a separate, password-only app client instead (see
 * `clientIdForDoor`). The card stays Cognito's on purpose: it owns first-login
 * password change, reset and MFA enrolment, which are the flows not worth
 * rebuilding in the console.
 *
 * The client door carries the address the sign-in page collected as
 * `login_hint`: managed login prefills its username field from it (verified on
 * the beta hosted UI, 2026-09-22), so the address is typed once. The employee
 * door carries nothing extra -- Cognito does not forward `login_hint` to
 * Google (verified the same day: the accounts.google.com URL arrives without
 * it), so adding it there would only be noise on the authorize request.
 *
 * Exported so a test can build the request with the real library and assert
 * the URL rather than trusting this comment.
 */
export function cognitoSigninArgs(
  path?: SignInPath,
  options?: SignInOptions,
): SigninRedirectArgs {
  if (path === "employee") {
    return { extraQueryParams: { identity_provider: "Google" } };
  }
  if (path === "client" && options?.loginHint) {
    return { extraQueryParams: { login_hint: options.loginHint } };
  }
  return {};
}

/**
 * What Cognito's /logout needs, which is not what oidc-client-ts sends.
 *
 * Cognito's sign-out endpoint is not OIDC RP-initiated logout. It wants
 * `client_id` and `logout_uri`, where `logout_uri` must be one of the app
 * client's registered sign-out URLs — the bare origins, matched exactly
 * (`https://beta.fleet.aerovect.com` passes; a trailing slash does not). Given
 * anything else it answers with a 302 to its own /login page carrying the
 * unknown parameters, which renders as an HTTP 400 error page.
 *
 * oidc-client-ts, left to itself, sends `id_token_hint` +
 * `post_logout_redirect_uri` + `state` and adds `client_id` only when there is
 * NO id_token_hint — and a signed-in user always has one. So every real sign-out
 * arrived at Cognito without a client_id: the user landed on an error page, and
 * because the library clears the local session *before* redirecting while
 * Cognito's hosted-UI cookie was never cleared, the next visit signed them
 * straight back in. That was "I can't log out of beta" (SW-7019).
 *
 * The client id is the one the session belongs to -- a sign-out sent with the
 * wrong app client's id is rejected the same way as one with none.
 *
 * Both parameters ride in `extraQueryParams`, which the library appends
 * verbatim. Cognito ignores the `id_token_hint` the library adds.
 * Exported so a test can build the request with the real library and assert
 * the URL, rather than trusting this comment.
 */
export function cognitoSignoutArgs(
  origin: string,
  clientId: string = currentClientId(),
): SignoutRedirectArgs {
  return {
    // Both through extraQueryParams, which the library appends verbatim. Its
    // own `client_id` argument exists at runtime but is missing from the
    // published SignoutRedirectArgs type; this keeps the call type-checked.
    extraQueryParams: { client_id: clientId, logout_uri: origin },
  };
}

export function CognitoAppAuthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  if (!cognitoConfigured()) {
    // Never fall back to a baked-in pool: a prod bundle silently pointed at the
    // beta pool is worse than a build that refuses to start.
    throw new Error(
      "REACT_APP_AUTH_PROVIDER=cognito requires REACT_APP_FLEET_STATE_COGNITO_AUTHORITY, _CLIENT_ID and _DOMAIN",
    );
  }
  return <AuthProvider {...buildConfig()}>{children}</AuthProvider>;
}

/**
 * Where a page load stands with respect to renewing a lapsed session.
 *
 * react-oidc-context reports `isAuthenticated` as "a user is stored AND its
 * access token has not expired", and oidc-client-ts schedules its own silent
 * renew only while the token still has time left. A page load more than an
 * hour after the last activity therefore settles as signed out with a valid
 * refresh token in storage, and RequireAuth showed the sign-in page (SW-7701).
 * One `signinSilent()` -- the refresh-token grant at the token endpoint --
 * turns that into a moment of spinner. It is tried once per page load: a
 * success loads the user into the provider; a failure (refresh token expired
 * or revoked, network) falls through to the sign-in page with a reason.
 */
type Renewal = "untried" | "pending" | "done" | "failed";

/** What a failed renewal is reported as; signInError.ts turns it into words. */
export const SESSION_LAPSED = "session expired: sign in again";

// One renewal per page load, shared by every component that uses the hook.
// PermissionsProvider, RequireAuth and the Navbar all mount before the session
// is known; each running its own signinSilent() sent three refresh grants for
// one visit, and with refresh-token rotation on, the second use of the same
// token is the reuse that rotation exists to refuse.
let sharedRenewal: {
  refreshToken: string;
  outcome: Promise<User | null>;
} | null = null;

function renewOnce(auth: AuthContextProps): Promise<User | null> {
  const refreshToken = auth.user?.refresh_token ?? "";
  if (!sharedRenewal || sharedRenewal.refreshToken !== refreshToken) {
    sharedRenewal = { refreshToken, outcome: auth.signinSilent() };
  }
  return sharedRenewal.outcome;
}

/** Tests render many page loads in one module; production never calls this. */
export function resetSessionRenewalForTests(): void {
  sharedRenewal = null;
}

export function useCognitoFleetAuth(): FleetAuthState {
  const auth = useAuth();
  const [renewal, setRenewal] = React.useState<Renewal>("untried");
  const [renewalError, setRenewalError] = React.useState<Error | undefined>();

  const renewable =
    renewal === "untried" &&
    !auth.isLoading &&
    !auth.isAuthenticated &&
    !auth.activeNavigator &&
    !auth.error &&
    Boolean(auth.user?.refresh_token);

  React.useEffect(() => {
    if (!renewable) return;
    setRenewal("pending");
    renewOnce(auth).then(
      (user) => {
        if (user) {
          setRenewal("done");
        } else {
          setRenewal("failed");
          setRenewalError(new Error(SESSION_LAPSED));
        }
      },
      (e: unknown) => {
        setRenewal("failed");
        setRenewalError(e instanceof Error ? e : new Error(String(e)));
      },
    );
    // `auth` is a fresh object on every provider update; `renewable` is the
    // guard that makes this run once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renewable]);

  return React.useMemo(
    () => ({
      // `activeNavigator` covers the redirect and silent-renew round trips, and
      // the renewal above holds "loading" from the first render, so a consumer
      // never sees a momentary "signed out" while a session is being renewed.
      isLoading:
        auth.isLoading ||
        Boolean(auth.activeNavigator) ||
        renewable ||
        renewal === "pending",
      isAuthenticated: auth.isAuthenticated,
      // react-oidc-context sets this for a failed signin callback and for a
      // failed silent renew alike; the gate only acts on it when there is no
      // session, which is the case that would otherwise loop. A failed
      // renewal on load is reported the same way.
      error: auth.error ?? renewalError,
      user: auth.user
        ? {
            email: auth.user.profile?.email,
            picture: auth.user.profile?.picture,
          }
        : undefined,
      // Already in hand: react-oidc-context refreshes it in the background, so
      // there is nothing to await. Async to match the Auth0 side's signature.
      //
      // Throws when absent rather than resolving undefined, because that is
      // what Auth0's getAccessTokenSilently does and every call site is already
      // written around it. Resolving undefined instead would send the string
      // "Bearer undefined" and turn a missing session into a 401 from the API,
      // which is a much worse way to learn the same thing.
      getAccessToken: async () => {
        const token = auth.user?.access_token;
        if (!token) {
          throw new Error(
            "no Cognito access token: the session is not established",
          );
        }
        return token;
      },
      login: (path, options) => {
        if (path) rememberDoor(path);
        const target = clientIdForDoor(path);
        // The mounted provider was built, at page load, for whichever client
        // the door pointed at THEN. Ask it -- not localStorage, which another
        // tab may have changed since -- which client that was. If this door
        // opens a different one, start the redirect from a UserManager built
        // for the target: the state it stores is keyed by the state id
        // (sessionStorage, shared across instances), and the callback page
        // load rebuilds the provider from the remembered door, so the client
        // that started the login is the one that finishes it -- oidc-client-ts
        // rejects a mismatch outright ("client_id mismatch on settings vs.
        // signin state").
        if (target !== mountedClientId(auth)) {
          // AuthProviderProps is a union that also admits a ready-made
          // UserManager; buildConfig only ever returns the settings shape.
          return new UserManager(
            buildConfig(target) as UserManagerSettings,
          ).signinRedirect(cognitoSigninArgs(path, options));
        }
        return auth.signinRedirect(cognitoSigninArgs(path, options));
      },
      logout: () => {
        // The Auth0 build's page-scoped Tractor State login leaves a Cognito
        // session in sessionStorage; harmless here, but a sign-out that leaves
        // any session behind is not one.
        clearPageScopedFleetStateSession();
        // The session belongs to the client the provider was built for; a
        // sign-out sent with any other client's id is rejected by Cognito.
        const args = cognitoSignoutArgs(
          window.location.origin,
          mountedClientId(auth),
        );
        // Forget the door before leaving, so the next visit starts on the
        // sign-in page with no client pre-selected.
        forgetDoor();
        void auth.signoutRedirect(args);
      },
    }),
    [auth, renewable, renewal, renewalError],
  );
}
